import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import {
  Bell, Plus, Trash2, Edit, Play, Mail, MessageSquare, 
  AlertTriangle, CheckCircle, Clock, Users, Settings,
  Shield, Zap, RefreshCw, X, Phone, Building
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_COLORS = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-green-500'
};

const SEVERITY_TEXT_COLORS = {
  critical: 'text-red-600',
  high: 'text-orange-600',
  medium: 'text-yellow-600',
  low: 'text-green-600'
};

const HazardAlertsPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [alertLogs, setAlertLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  
  const [newConfig, setNewConfig] = useState({
    name: '',
    enabled: true,
    severity_triggers: ['critical', 'high'],
    notification_channels: ['email', 'in_app'],
    contacts: [{ name: '', email: '', phone: '', role: '' }],
    locations: [],
    cooldown_minutes: 15,
    include_image: true,
    include_recommendations: true
  });

  const fetchData = useCallback(async () => {
    try {
      const [configsRes, logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/hazard-alerts/config`),
        axios.get(`${API}/api/hazard-alerts/logs?limit=20`),
        axios.get(`${API}/api/hazard-alerts/stats`)
      ]);
      
      setConfigs(configsRes.data.configs || []);
      setAlertLogs(logsRes.data.logs || []);
      setStats(statsRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load alert data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateConfig = async () => {
    try {
      // Filter out empty contacts
      const validContacts = newConfig.contacts.filter(c => c.name && (c.email || c.phone));
      
      if (validContacts.length === 0) {
        toast({ title: 'Error', description: 'Please add at least one contact', variant: 'destructive' });
        return;
      }
      
      await axios.post(`${API}/api/hazard-alerts/config`, {
        ...newConfig,
        contacts: validContacts
      });
      
      toast({ title: 'Success', description: 'Alert configuration created' });
      setShowCreateDialog(false);
      setNewConfig({
        name: '',
        enabled: true,
        severity_triggers: ['critical', 'high'],
        notification_channels: ['email', 'in_app'],
        contacts: [{ name: '', email: '', phone: '', role: '' }],
        locations: [],
        cooldown_minutes: 15,
        include_image: true,
        include_recommendations: true
      });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create configuration', variant: 'destructive' });
    }
  };

  const handleToggleConfig = async (configId) => {
    try {
      await axios.post(`${API}/api/hazard-alerts/config/${configId}/toggle`);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to toggle configuration', variant: 'destructive' });
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this alert configuration?')) return;
    
    try {
      await axios.delete(`${API}/api/hazard-alerts/config/${configId}`);
      toast({ title: 'Success', description: 'Configuration deleted' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete configuration', variant: 'destructive' });
    }
  };

  const handleTestAlert = async (configId) => {
    try {
      toast({ title: 'Sending Test', description: 'Sending test alert...' });
      const response = await axios.post(`${API}/api/hazard-alerts/test/${configId}`);
      
      if (response.data.success) {
        toast({ title: 'Test Sent', description: `Test alert sent via ${response.data.channels_tested.map(c => c.channel).join(', ')}` });
      } else {
        toast({ title: 'Warning', description: 'Test sent but some channels may have failed', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to send test alert', variant: 'destructive' });
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`${API}/api/hazard-alerts/logs/${alertId}/acknowledge`);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to acknowledge alert', variant: 'destructive' });
    }
  };

  const handleAcknowledgeAll = async () => {
    try {
      await axios.post(`${API}/api/hazard-alerts/logs/acknowledge-all`);
      toast({ title: 'Success', description: 'All alerts acknowledged' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to acknowledge alerts', variant: 'destructive' });
    }
  };

  const addContact = () => {
    setNewConfig(prev => ({
      ...prev,
      contacts: [...prev.contacts, { name: '', email: '', phone: '', role: '' }]
    }));
  };

  const removeContact = (index) => {
    setNewConfig(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
  };

  const updateContact = (index, field, value) => {
    setNewConfig(prev => ({
      ...prev,
      contacts: prev.contacts.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="hazard-alerts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-red-600" />
            Real-Time Hazard Alerts
          </h1>
          <p className="text-slate-500 mt-1">
            Get instant notifications when critical hazards are detected
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="create-alert-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Alert Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Alert Configuration</DialogTitle>
              <DialogDescription>
                Set up real-time notifications for hazard detections
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Basic Info */}
              <div>
                <Label>Alert Name *</Label>
                <Input 
                  value={newConfig.name}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Critical Hazard Alerts - Building A"
                />
              </div>
              
              {/* Severity Triggers */}
              <div>
                <Label className="mb-2 block">Trigger on Severity Levels</Label>
                <div className="flex flex-wrap gap-2">
                  {['critical', 'high', 'medium', 'low'].map(sev => (
                    <button
                      key={sev}
                      onClick={() => {
                        setNewConfig(prev => ({
                          ...prev,
                          severity_triggers: prev.severity_triggers.includes(sev)
                            ? prev.severity_triggers.filter(s => s !== sev)
                            : [...prev.severity_triggers, sev]
                        }));
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        newConfig.severity_triggers.includes(sev)
                          ? `${SEVERITY_COLORS[sev]} text-white`
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {sev.charAt(0).toUpperCase() + sev.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Notification Channels */}
              <div>
                <Label className="mb-2 block">Notification Channels</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newConfig.notification_channels.includes('email')}
                      onChange={(e) => {
                        setNewConfig(prev => ({
                          ...prev,
                          notification_channels: e.target.checked
                            ? [...prev.notification_channels, 'email']
                            : prev.notification_channels.filter(c => c !== 'email')
                        }));
                      }}
                      className="rounded"
                    />
                    <Mail className="w-4 h-4" />
                    Email
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newConfig.notification_channels.includes('in_app')}
                      onChange={(e) => {
                        setNewConfig(prev => ({
                          ...prev,
                          notification_channels: e.target.checked
                            ? [...prev.notification_channels, 'in_app']
                            : prev.notification_channels.filter(c => c !== 'in_app')
                        }));
                      }}
                      className="rounded"
                    />
                    <Bell className="w-4 h-4" />
                    In-App
                  </label>
                </div>
              </div>
              
              {/* Contacts */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Alert Recipients *</Label>
                  <Button variant="outline" size="sm" onClick={addContact}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Contact
                  </Button>
                </div>
                <div className="space-y-3">
                  {newConfig.contacts.map((contact, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Name"
                          value={contact.name}
                          onChange={(e) => updateContact(index, 'name', e.target.value)}
                        />
                        <Input
                          placeholder="Email"
                          type="email"
                          value={contact.email}
                          onChange={(e) => updateContact(index, 'email', e.target.value)}
                        />
                        <Input
                          placeholder="Phone (optional)"
                          value={contact.phone}
                          onChange={(e) => updateContact(index, 'phone', e.target.value)}
                        />
                        <Input
                          placeholder="Role (optional)"
                          value={contact.role}
                          onChange={(e) => updateContact(index, 'role', e.target.value)}
                        />
                      </div>
                      {newConfig.contacts.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeContact(index)}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Cooldown */}
              <div>
                <Label>Alert Cooldown (minutes)</Label>
                <Input
                  type="number"
                  min="0"
                  max="1440"
                  value={newConfig.cooldown_minutes}
                  onChange={(e) => setNewConfig(prev => ({ ...prev, cooldown_minutes: parseInt(e.target.value) || 15 }))}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum time between alerts for the same location
                </p>
              </div>
              
              {/* Options */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <Switch
                    checked={newConfig.include_recommendations}
                    onCheckedChange={(v) => setNewConfig(prev => ({ ...prev, include_recommendations: v }))}
                  />
                  Include Recommendations
                </label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateConfig} disabled={!newConfig.name} className="bg-red-600 hover:bg-red-700">
                Create Alert Rule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Active Rules</p>
                <p className="text-3xl font-bold text-blue-900">{stats?.active_configs || 0}</p>
              </div>
              <Settings className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Unacknowledged</p>
                <p className="text-3xl font-bold text-red-900">{stats?.unacknowledged || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Last 24 Hours</p>
                <p className="text-3xl font-bold text-amber-900">{stats?.alerts_last_24h || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Total Alerts</p>
                <p className="text-3xl font-bold text-green-900">{stats?.total_alerts || 0}</p>
              </div>
              <Bell className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="configs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="configs" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Alert Rules
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Alert History
            {stats?.unacknowledged > 0 && (
              <Badge className="bg-red-500 ml-1">{stats.unacknowledged}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Alert Rules Tab */}
        <TabsContent value="configs" className="space-y-4">
          {configs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bell className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Alert Rules Configured</h3>
                <p className="text-gray-500 mb-4">
                  Set up real-time alerts to get notified when critical hazards are detected
                </p>
                <Button onClick={() => setShowCreateDialog(true)} className="bg-red-600 hover:bg-red-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Alert Rule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {configs.map((config) => (
                <Card key={config.id} className={`${!config.enabled ? 'opacity-60' : ''}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{config.name}</h3>
                          <Badge variant={config.enabled ? "default" : "secondary"}>
                            {config.enabled ? 'Active' : 'Paused'}
                          </Badge>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Triggers: {config.severity_triggers?.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {config.contacts?.length || 0} recipient(s)
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {config.cooldown_minutes}min cooldown
                          </span>
                        </div>
                        
                        <div className="flex gap-2 mt-3">
                          {config.notification_channels?.includes('email') && (
                            <Badge variant="outline" className="bg-blue-50">
                              <Mail className="w-3 h-3 mr-1" /> Email
                            </Badge>
                          )}
                          {config.notification_channels?.includes('in_app') && (
                            <Badge variant="outline" className="bg-purple-50">
                              <Bell className="w-3 h-3 mr-1" /> In-App
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestAlert(config.id)}
                          title="Send Test Alert"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleConfig(config.id)}
                        >
                          {config.enabled ? 'Pause' : 'Enable'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteConfig(config.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Alert History Tab */}
        <TabsContent value="logs" className="space-y-4">
          {alertLogs.length > 0 && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={handleAcknowledgeAll}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Acknowledge All
              </Button>
            </div>
          )}
          
          {alertLogs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-green-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Alerts Yet</h3>
                <p className="text-gray-500">
                  Alert history will appear here when hazards trigger notifications
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {alertLogs.map((log) => (
                <Card key={log.id} className={`${log.acknowledged ? 'bg-gray-50' : 'border-l-4 border-l-red-500'}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={SEVERITY_COLORS[log.severity]}>
                            {log.severity.toUpperCase()}
                          </Badge>
                          <span className="font-medium">{log.location}</span>
                          {log.acknowledged && (
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" /> Acknowledged
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {log.hazard_count} hazard(s) detected • Notified via {log.channels_notified?.join(', ')} • 
                          {log.contacts_notified} contact(s)
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(log.created_at).toLocaleString()}
                          {log.config_name && ` • ${log.config_name}`}
                        </p>
                      </div>
                      
                      {!log.acknowledged && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAcknowledgeAlert(log.id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Acknowledge
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HazardAlertsPage;
